// 必要なモジュールを読み込み
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
export class ThreeApp {
  /**
   * カメラ定義のための定数
   */
  static CAMERA_PARAM = {
    fovy: 60,
    aspect: window.innerWidth / window.innerHeight,
    near: 0.1,
    far: 50.0,
    position: new THREE.Vector3(0.0, 2.0, 10.0),
    lookAt: new THREE.Vector3(0.0, 0.0, 0.0),
  };
  /**
   * レンダラー定義のための定数
   */
  static RENDERER_PARAM = {
    clearColor: 0xffffff,
  };
  static LIGHT_PARAM = {
    color: 0xffffff,
    intensity: 4.0,
  };

  canvas: HTMLCanvasElement; // 既存のcanvas要素
  renderer!: THREE.WebGLRenderer; // レンダラ
  scene!: THREE.Scene; // シーン
  camera!: THREE.PerspectiveCamera; // カメラ
  gltf!: any; // 読み込んだ glTF 格納用
  matcapMaterial!: THREE.MeshMatcapMaterial; // マテリアル用テクスチャ
  mixer!: THREE.AnimationMixer; // アニメーションミキサー
  actions!: THREE.AnimationAction[]; // アニメーションのアクション
  clock!: THREE.Clock; // アニメーション用のクロック
  ambientLight!: THREE.AmbientLight; // 環境光
  directionalLight!: THREE.DirectionalLight; // 方向性ライト

  // メッシュアニメーション用プロパティ
  meshes: THREE.Mesh[] = []; // モデル内の全メッシュ
  meshOriginalPositions: THREE.Vector3[] = []; // 各メッシュの元の位置
  meshAnimationStates: Array<{ startTime: number; isAnimating: boolean; isComplete: boolean }> = []; // 各メッシュのアニメーション状態
  animationStartTime: number = 0; // アニメーション開始時刻
  meshDisplayDelay: number = 0.01; // グループ内のメッシュ間遅延（秒）
  groupDelay: number = 0.01; // グループ間の遅延（秒）
  meshesPerGroup: number = 10; // 一度に落とすメッシュ数
  fallDuration: number = 0.4; // 落下アニメーションの持続時間（秒）
  fallHeight: number = 30; // 落下開始の高さ
  excludedMeshCount: number = 138; // 最後から除外するメッシュ数
  isAnimationComplete: boolean = false; // アニメーション完了フラグ

  // 揺れアニメーション用プロパティ
  swayAmplitude: number = 0.3; // 揺れの振幅
  swaySpeed: number = 2.0; // 揺れの速度
  swaySteps: number = 6; // 段階的な動きのステップ数
  swayStepDuration: number = 0.15; // 各ステップの持続時間（秒）

  /**
   * コンストラクタ
   * @constructor
   * @param {HTMLCanvasElement} canvas - 既存のcanvas要素
   */
  constructor(canvas: HTMLCanvasElement) {
    // 既存のcanvas要素を保持
    this.canvas = canvas;

    // this のバインド
    this.render = this.render.bind(this);

    // ウィンドウのリサイズを検出できるようにする
    window.addEventListener(
      "resize",
      () => {
        this.updateSize();
      },
      false
    );

    // restartButtonのイベントリスナーを追加
    const restartButton = document.querySelector("#restartButton") as HTMLButtonElement | null;
    if (restartButton) {
      restartButton.addEventListener("click", () => {
        if (this) {
          this.restartMeshAnimation();
        }
      });
    }
  }

  /**
   * サイズを更新する処理
   */
  updateSize() {
    const rect = this.canvas.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;

    this.renderer.setSize(width, height);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
  }

  /**
   * 初期化処理
   */
  init() {
    // canvas要素のサイズを取得
    const rect = this.canvas.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;

    // レンダラー（既存のcanvasを使用）
    const color = new THREE.Color(ThreeApp.RENDERER_PARAM.clearColor);
    this.renderer = new THREE.WebGLRenderer({ canvas: this.canvas, antialias: true });
    this.renderer.setClearColor(color);
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.setSize(width, height);

    // シーン
    this.scene = new THREE.Scene();

    // カメラ
    this.camera = new THREE.PerspectiveCamera(
      ThreeApp.CAMERA_PARAM.fovy,
      width / height,
      ThreeApp.CAMERA_PARAM.near,
      ThreeApp.CAMERA_PARAM.far
    );
    this.camera.position.copy(ThreeApp.CAMERA_PARAM.position);
    this.camera.lookAt(ThreeApp.CAMERA_PARAM.lookAt);

    // 方向性ライトを追加
    this.directionalLight = new THREE.DirectionalLight(
      ThreeApp.LIGHT_PARAM.color,
      ThreeApp.LIGHT_PARAM.intensity
    );
    this.directionalLight.position.set(5, 5, 5);
    this.scene.add(this.directionalLight);

    // clock
    this.clock = new THREE.Clock();

    // アニメーション開始時刻を記録
    this.animationStartTime = performance.now();
  }
  /**
   * アセット（素材）のロードを行う Promise
   */
  load() {
    return new Promise<void>((resolve) => {
      const canvasEl = document.getElementById("stansEng3D") as HTMLCanvasElement | null;
      const assetsBase = (canvasEl?.dataset.assetsBase || "").replace(/\/$/, "");
      const gltfPath = `${assetsBase}/images/pages/work-007/stans-eng-3d.glb`;
      const loader = new GLTFLoader();
      loader.load(
        gltfPath,
        (gltf) => {
          this.gltf = gltf;
          resolve();
        },
        undefined,
        (error) => {
          resolve();
        }
      );
    });
  }

  /**
   * GLTFモデルをシーンに追加する処理
   */
  setupModel() {
    if (!this.gltf || !this.scene) {
      return;
    }

    // GLTFモデルをシーンに追加
    this.scene.add(this.gltf.scene);

    this.gltf.scene.rotation.y = Math.PI * -1.12;
    this.gltf.scene.scale.set(0.3, 0.3, 0.3);
    this.gltf.scene.position.y = -3.5;
    this.gltf.scene.position.x = -1.0;

    // モデル内のすべてのメッシュを収集
    this.collectMeshes(this.gltf.scene);

    // アニメーション対象のメッシュ数を計算（最後の150個を除外）
    const animationMeshCount = Math.max(0, this.meshes.length - this.excludedMeshCount);

    // 各メッシュの元の位置を保存し、アニメーション設定
    this.meshes.forEach((mesh, index) => {
      // 元の位置を保存
      this.meshOriginalPositions[index] = mesh.position.clone();

      // アニメーション対象かどうかで処理を分ける
      if (index < animationMeshCount) {
        // アニメーション対象: 上方に移動して非表示
        mesh.position.y += this.fallHeight;
        mesh.visible = false;

        // アニメーション状態を初期化
        this.meshAnimationStates[index] = {
          startTime: 0,
          isAnimating: false,
          isComplete: false,
        };
      } else {
        // 除外対象: 元の位置で表示
        mesh.visible = true;

        // アニメーション状態は最初から完了扱い
        this.meshAnimationStates[index] = {
          startTime: 0,
          isAnimating: false,
          isComplete: true,
        };
      }
    });

    // アニメーション開始時刻をリセット
    this.animationStartTime = performance.now();
    this.isAnimationComplete = false;
  }

  /**
   * オブジェクト内のすべてのメッシュを再帰的に収集
   */
  collectMeshes(object: THREE.Object3D) {
    if (object instanceof THREE.Mesh) {
      this.meshes.push(object);
    }

    object.children.forEach((child) => {
      this.collectMeshes(child);
    });
  }

  /**
   * 描画処理
   */
  render() {
    // 恒常ループ
    requestAnimationFrame(this.render);

    // メッシュアニメーションを更新
    this.updateMeshAnimation();

    // 揺れアニメーションを更新
    this.updateSwayAnimation();

    // 通常のレンダリング
    this.renderer.render(this.scene, this.camera);
  }

  /**
   * メッシュを順番に落下させるアニメーション
   */
  updateMeshAnimation() {
    if (this.isAnimationComplete || this.meshes.length === 0) {
      return;
    }

    const currentTime = performance.now();
    const elapsedTime = (currentTime - this.animationStartTime) / 1000; // 秒に変換

    let allComplete = true;

    // アニメーション対象のメッシュ数を計算
    const animationMeshCount = Math.max(0, this.meshes.length - this.excludedMeshCount);

    this.meshes.forEach((mesh, index) => {
      const state = this.meshAnimationStates[index];

      // アニメーション対象外のメッシュはスキップ
      if (index >= animationMeshCount) {
        return;
      }

      // グループ番号とグループ内のインデックスを計算
      const groupIndex = Math.floor(index / this.meshesPerGroup);
      const indexInGroup = index % this.meshesPerGroup;

      // グループの開始時間を計算
      const groupStartTime = groupIndex * this.groupDelay;
      const meshStartTime = groupStartTime + indexInGroup * this.meshDisplayDelay;

      // メッシュのアニメーション開始タイミングかチェック
      if (elapsedTime >= meshStartTime && !state.isAnimating && !state.isComplete) {
        // アニメーション開始
        state.isAnimating = true;
        state.startTime = currentTime;
        mesh.visible = true;
      }

      // アニメーション中のメッシュの位置を更新
      if (state.isAnimating && !state.isComplete) {
        const animationElapsed = (currentTime - state.startTime) / 1000;
        const progress = Math.min(animationElapsed / this.fallDuration, 1.0);

        // Y位置を計算（上から元の位置まで）- リニア（等速直線運動）
        const startY = this.meshOriginalPositions[index].y + this.fallHeight;
        const endY = this.meshOriginalPositions[index].y;
        mesh.position.y = startY + (endY - startY) * progress;

        // アニメーション完了チェック
        if (progress >= 1.0) {
          state.isAnimating = false;
          state.isComplete = true;
          mesh.position.y = endY; // 最終位置を確定
        }
      }

      // まだ完了していないメッシュがあるかチェック
      if (!state.isComplete) {
        allComplete = false;
      }
    });

    // すべてのメッシュのアニメーションが完了したら終了
    if (allComplete) {
      this.isAnimationComplete = true;
    }
  }

  /**
   * メッシュアニメーションを再開始
   */
  restartMeshAnimation() {
    // アニメーション対象のメッシュ数を計算
    const animationMeshCount = Math.max(0, this.meshes.length - this.excludedMeshCount);

    this.meshes.forEach((mesh, index) => {
      if (index < animationMeshCount) {
        // アニメーション対象: 上方に移動して非表示
        mesh.visible = false;
        mesh.position.y = this.meshOriginalPositions[index].y + this.fallHeight;

        // アニメーション状態をリセット
        this.meshAnimationStates[index] = {
          startTime: 0,
          isAnimating: false,
          isComplete: false,
        };
      } else {
        // 除外対象: 元の位置で表示を維持
        mesh.position.copy(this.meshOriginalPositions[index]);
        mesh.visible = true;

        this.meshAnimationStates[index] = {
          startTime: 0,
          isAnimating: false,
          isComplete: true,
        };
      }
    });

    // アニメーション状態をリセット
    this.animationStartTime = performance.now();
    this.isAnimationComplete = false;
  }

  /**
   * 除外されたメッシュの揺れアニメーション
   */
  updateSwayAnimation() {
    const currentTime = performance.now() / 1000; // 秒に変換
    const animationMeshCount = Math.max(0, this.meshes.length - this.excludedMeshCount);

    // 段階的な動きを計算（往復動作）
    const totalSteps = this.swaySteps * 2 - 2; // 往復で重複を除いたステップ数
    const cycleDuration = this.swayStepDuration * totalSteps; // 1往復サイクルの時間
    const timeInCycle = (currentTime * this.swaySpeed) % cycleDuration; // サイクル内の時間
    const currentStep = Math.floor(timeInCycle / this.swayStepDuration); // 現在のステップ

    // 往復動作のための位置計算
    // 行き: 0 → 1 → 2 → 3 → 4 → 5
    // 戻り: 4 → 3 → 2 → 1 → 0 (5は除く)
    const stepInCycle = currentStep % totalSteps;

    let normalizedStep;
    if (stepInCycle < this.swaySteps) {
      // 行き: 0から5まで
      normalizedStep = stepInCycle;
    } else {
      // 戻り: 4から0まで
      normalizedStep = this.swaySteps - 2 - (stepInCycle - this.swaySteps);
    }

    // -1.0から1.0までの位置を計算
    const stepPosition = (normalizedStep / (this.swaySteps - 1)) * 2 - 1;
    const swayOffset = stepPosition * this.swayAmplitude;

    // 除外されたメッシュ（最後の138個）のみ処理
    for (let index = animationMeshCount; index < this.meshes.length; index++) {
      const mesh = this.meshes[index];
      const originalPosition = this.meshOriginalPositions[index];

      if (mesh && originalPosition) {
        // X座標に段階的な揺れを適用
        mesh.position.x = originalPosition.x + swayOffset;
        mesh.position.y = originalPosition.y;
        mesh.position.z = originalPosition.z;
      }
    }
  }
}
